import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Copy, Trash2, Sparkles, Eye } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const CATEGORIES = ["Strategy", "Products", "SBA", "Credit", "Playbook", "Lender Basics", "Buyer's guide"];

const parseDraft = (content) => {
  const m = content.match(/TITLE:\s*(.*)\s*\nMETA:\s*([\s\S]*?)\s*\nSLUG:\s*(.*)\s*\n---\n?([\s\S]*)/);
  if (!m) return { title: null, metaDesc: null, slug: null, body: content };
  return { title: m[1].trim(), metaDesc: m[2].trim(), slug: m[3].trim(), body: m[4].trim() };
};

const btnBase = "px-4 py-2 font-mono text-[11px] tracking-[0.24em] uppercase border transition-colors";
const btnOn = "bg-[color:var(--gold)] text-black border-[color:var(--gold)]";
const btnOff = "border-[color:var(--bone)]/20 text-[color:var(--bone)]/70 hover:border-[color:var(--gold)] hover:text-[color:var(--gold)]";

export const AiWriter = ({ api, token }) => {
  const [topic, setTopic] = useState("");
  const [category, setCategory] = useState("Strategy");
  const [keyPoints, setKeyPoints] = useState("");
  const [mode, setMode] = useState("standard");
  const [generating, setGenerating] = useState(false);
  const [output, setOutput] = useState("");
  const [drafts, setDrafts] = useState([]);
  const [openDraft, setOpenDraft] = useState(null);
  const [preview, setPreview] = useState(null); // { category, mode, ...parseDraft }
  const outRef = useRef(null);

  const openPreview = (content, cat, m) => setPreview({ ...parseDraft(content), category: cat, mode: m });

  const loadDrafts = async () => {
    try {
      const r = await axios.get(`${api}/admin/ai/drafts`, { headers: { "X-Admin-Token": token } });
      setDrafts(r.data.drafts || []);
    } catch {
      toast.error("Failed to load drafts");
    }
  };

  useEffect(() => { loadDrafts(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (outRef.current) outRef.current.scrollTop = outRef.current.scrollHeight;
  }, [output]);

  const generate = async () => {
    if (!topic.trim()) { toast.error("Give me a topic first"); return; }
    setGenerating(true);
    setOutput("");
    try {
      const res = await fetch(`${api}/admin/ai/draft`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Admin-Token": token },
        body: JSON.stringify({ topic, category, key_points: keyPoints, mode }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const events = buf.split("\n\n");
        buf = events.pop();
        for (const ev of events) {
          if (!ev.startsWith("data: ")) continue;
          const j = JSON.parse(ev.slice(6));
          if (j.delta) setOutput((o) => o + j.delta);
          if (j.error) { toast.error(j.error); }
          if (j.done) { toast.success("Draft saved to your library."); loadDrafts(); }
        }
      }
    } catch {
      toast.error("Generation failed. Please try again.");
    } finally {
      setGenerating(false);
    }
  };

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard.");
    } catch {
      toast.error("Copy failed — select and copy manually.");
    }
  };

  const removeDraft = async (id) => {
    try {
      await axios.delete(`${api}/admin/ai/drafts/${id}`, { headers: { "X-Admin-Token": token } });
      setDrafts((d) => d.filter((x) => x.id !== id));
      if (openDraft?.id === id) setOpenDraft(null);
      toast.success("Draft deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const wordCount = output ? output.trim().split(/\s+/).length : 0;

  return (
    <div className="mt-10 grid lg:grid-cols-[420px_1fr] gap-10" data-testid="ai-writer-panel">
      {/* Left — form */}
      <div>
        <div className="overline">AI Writer · Claude Fable 5</div>
        <h2 className="mt-3 font-serif text-3xl">Draft a new article</h2>

        <label className="block mt-8 font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/50">Topic *</label>
        <input
          data-testid="ai-topic-input"
          className="editorial-input"
          placeholder="e.g. How seasonal businesses should time a line of credit"
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          disabled={generating}
        />

        <label className="block mt-6 font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/50">Category</label>
        <div className="mt-2 flex flex-wrap gap-2">
          {CATEGORIES.map((c) => (
            <button key={c} data-testid={`ai-category-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              onClick={() => setCategory(c)} disabled={generating}
              className={`${btnBase} ${category === c ? btnOn : btnOff}`}>
              {c}
            </button>
          ))}
        </div>

        <label className="block mt-6 font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/50">Key points to cover (optional)</label>
        <textarea
          data-testid="ai-keypoints-input"
          className="editorial-input min-h-[96px]"
          placeholder={"One per line, e.g.\n- soft vs hard credit pulls\n- what lenders look at first"}
          value={keyPoints}
          onChange={(e) => setKeyPoints(e.target.value)}
          disabled={generating}
        />

        <label className="block mt-6 font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/50">Length</label>
        <div className="mt-2 flex gap-2">
          <button data-testid="ai-mode-standard" onClick={() => setMode("standard")} disabled={generating}
            className={`${btnBase} ${mode === "standard" ? btnOn : btnOff}`}>
            Standard · ~1,400 words
          </button>
          <button data-testid="ai-mode-pillar" onClick={() => setMode("pillar")} disabled={generating}
            className={`${btnBase} ${mode === "pillar" ? btnOn : btnOff}`}>
            Pillar · 3,000+ w/ spin-off map
          </button>
        </div>

        <button
          data-testid="ai-generate-btn"
          onClick={generate}
          disabled={generating}
          className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-[color:var(--gold)] text-black px-6 py-3.5 font-mono text-xs tracking-[0.24em] uppercase hover:bg-[color:var(--gold-strong)] transition-colors disabled:opacity-60"
        >
          <Sparkles size={14} />
          {generating ? "Writing…" : "Generate draft"}
        </button>
        <p className="mt-3 font-mono text-[10px] tracking-[0.18em] uppercase text-[color:var(--bone)]/45">
          Billed to your Universal Key balance · ≈ $0.15–0.40 per article
        </p>

        {/* Drafts library */}
        <div className="mt-12 border-t border-black/10 pt-8">
          <div className="overline">Library · {drafts.length} saved</div>
          <ul className="mt-4 space-y-3">
            {drafts.map((d) => (
              <li key={d.id} data-testid="ai-draft-row" className="border border-black/10 bg-[color:var(--bg-elev)] p-4">
                <div className="flex items-start justify-between gap-3">
                  <button className="text-left min-w-0" onClick={() => setOpenDraft(openDraft?.id === d.id ? null : d)}>
                    <div className="font-serif text-lg leading-snug">{d.topic}</div>
                    <div className="mt-1 font-mono text-[10px] tracking-[0.2em] uppercase text-[color:var(--bone)]/50">
                      {d.category} · {d.mode} · {new Date(d.created_at).toLocaleDateString()}
                    </div>
                  </button>
                  <div className="flex gap-2 flex-shrink-0">
                    <button data-testid="ai-draft-preview" title="Preview as blog post" onClick={() => openPreview(d.content, d.category, d.mode)}
                      className="p-2 border border-[color:var(--bone)]/20 hover:border-[color:var(--gold)] hover:text-[color:var(--gold)] transition-colors">
                      <Eye size={13} />
                    </button>
                    <button data-testid="ai-draft-copy" title="Copy markdown" onClick={() => copyText(d.content)}
                      className="p-2 border border-[color:var(--bone)]/20 hover:border-[color:var(--gold)] hover:text-[color:var(--gold)] transition-colors">
                      <Copy size={13} />
                    </button>
                    <button data-testid="ai-draft-delete" title="Delete draft" onClick={() => removeDraft(d.id)}
                      className="p-2 border border-[color:var(--bone)]/20 hover:border-red-700 hover:text-red-700 transition-colors">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
                {openDraft?.id === d.id && (
                  <pre className="mt-4 max-h-64 overflow-y-auto whitespace-pre-wrap font-mono text-xs leading-relaxed text-[color:var(--bone)]/80 border-t border-black/10 pt-4">
                    {d.content}
                  </pre>
                )}
              </li>
            ))}
            {drafts.length === 0 && (
              <li className="py-6 text-center font-mono text-xs tracking-[0.22em] uppercase text-[color:var(--bone)]/45">
                No drafts yet — generate your first
              </li>
            )}
          </ul>
        </div>
      </div>

      {/* Right — live output */}
      <div className="min-w-0">
        <div className="flex items-center justify-between">
          <div className="overline">Live draft {wordCount > 0 && `· ${wordCount.toLocaleString()} words`}</div>
          {output && !generating && (
            <div className="flex gap-2">
              <button data-testid="ai-output-preview" onClick={() => openPreview(output, category, mode)}
                className="inline-flex items-center gap-2 border border-[color:var(--gold)] text-[color:var(--gold)] px-4 py-2 font-mono text-[11px] tracking-[0.24em] uppercase hover:bg-[color:var(--gold)] hover:text-black transition-colors">
                <Eye size={13} /> Preview
              </button>
              <button data-testid="ai-output-copy" onClick={() => copyText(output)}
                className="inline-flex items-center gap-2 border border-[color:var(--gold)] text-[color:var(--gold)] px-4 py-2 font-mono text-[11px] tracking-[0.24em] uppercase hover:bg-[color:var(--gold)] hover:text-black transition-colors">
                <Copy size={13} /> Copy markdown
              </button>
            </div>
          )}
        </div>
        <div
          ref={outRef}
          data-testid="ai-stream-output"
          className="mt-4 h-[70vh] overflow-y-auto border border-black/10 bg-[color:var(--bg-elev)] p-6 whitespace-pre-wrap font-mono text-[13px] leading-relaxed text-[color:var(--bone)]/90"
        >
          {output || (generating ? "Claude is writing…" : "Your draft will stream here in real time.")}
          {generating && <span className="inline-block w-2 h-4 bg-[color:var(--gold)] ml-1 animate-pulse" />}
        </div>
        {output && !generating && (
          <p className="mt-3 text-sm text-[color:var(--bone)]/60">
            To publish: copy the markdown and paste it to your Emergent agent in chat — it will be added to the blog with full SEO wiring.
          </p>
        )}
      </div>

      {/* Blog-style preview */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent data-testid="ai-preview-dialog" className="max-w-4xl max-h-[88vh] overflow-y-auto bg-[color:var(--bg)] border border-black/10 text-[color:var(--bone)] p-0">
          {preview && (
            <article className="px-8 md:px-14 py-12">
              <DialogHeader>
                <div className="overline">
                  Preview · {preview.category} · {preview.mode === "pillar" ? "Pillar" : "Standard"}
                </div>
                <DialogTitle asChild>
                  <h1 className="mt-5 font-serif text-3xl md:text-5xl leading-[1.05] tracking-tight font-normal">
                    {preview.title || "Untitled draft"}
                  </h1>
                </DialogTitle>
              </DialogHeader>
              {preview.metaDesc && (
                <p className="mt-6 text-[color:var(--bone)]/75 text-lg leading-relaxed">{preview.metaDesc}</p>
              )}
              <div className="mt-6 flex items-center gap-4 font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/55 border-b border-black/10 pb-8">
                <span>Steady Path Editorial</span>
                {preview.slug && (
                  <>
                    <span className="text-[color:var(--gold)]">·</span>
                    <span className="normal-case tracking-normal">/blog/{preview.slug}</span>
                  </>
                )}
              </div>
              <div className="prose-editorial mt-10">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{preview.body}</ReactMarkdown>
              </div>
            </article>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AiWriter;
